// ============================================================
// pages/admin/AdminFinancials.jsx — Financial Reports
// ============================================================

import React, { useState, useMemo } from "react";
import { useBookings } from "../../hooks/useFirestore";

export default function AdminFinancials() {
  const { bookings } = useBookings(true);
  const [dateRange, setDateRange] = useState("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  // Filter bookings by date range
  const filteredBookings = useMemo(() => {
    const completed = bookings.filter((b) => ["completed", "delivered"].includes(b.status));
    if (dateRange === "all") return completed;

    const now = new Date();
    let from;
    if (dateRange === "today") {
      from = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    } else if (dateRange === "week") {
      from = new Date(now.getTime() - 7 * 86400000);
    } else if (dateRange === "month") {
      from = new Date(now.getFullYear(), now.getMonth(), 1);
    } else if (dateRange === "year") {
      from = new Date(now.getFullYear(), 0, 1);
    } else if (dateRange === "custom" && customFrom) {
      from = new Date(customFrom);
    }

    const to = dateRange === "custom" && customTo ? new Date(customTo + "T23:59:59") : now;

    return completed.filter((b) => {
      const d = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
      return d >= from && d <= to;
    });
  }, [bookings, dateRange, customFrom, customTo]);

  // Calculate metrics
  const totalRevenue = filteredBookings.reduce((sum, b) => sum + (b.total || 0), 0);
  const totalOrders = filteredBookings.length;
  const avgOrderValue = totalOrders > 0 ? Math.round(totalRevenue / totalOrders) : 0;
  const totalDiscount = filteredBookings.reduce((sum, b) => sum + (b.discount || 0), 0);
  const totalDeliveryFees = filteredBookings.reduce((sum, b) => sum + (b.deliveryFee || 0), 0);
  const deliveryOrders = filteredBookings.filter((b) => b.deliveryMethod === "delivery").length;
  const pickupOrders = filteredBookings.filter((b) => b.deliveryMethod === "pickup").length;

  // Product breakdown
  const productStats = useMemo(() => {
    const map = {};
    filteredBookings.forEach((b) => {
      (b.items || []).forEach((item) => {
        const key = item.productName;
        if (!map[key]) map[key] = { name: key, quantity: 0, revenue: 0, orders: 0 };
        map[key].quantity += item.quantity;
        map[key].revenue += item.subtotal || 0;
        map[key].orders += 1;
      });
    });
    return Object.values(map).sort((a, b) => b.revenue - a.revenue);
  }, [filteredBookings]);

  // Daily revenue breakdown
  const dailyRevenue = useMemo(() => {
    const map = {};
    filteredBookings.forEach((b) => {
      const d = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
      const key = d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
      if (!map[key]) map[key] = { date: key, revenue: 0, orders: 0, sortDate: d };
      map[key].revenue += b.total || 0;
      map[key].orders += 1;
    });
    return Object.values(map).sort((a, b) => b.sortDate - a.sortDate);
  }, [filteredBookings]);

  // Monthly revenue breakdown
  const monthlyRevenue = useMemo(() => {
    const map = {};
    filteredBookings.forEach((b) => {
      const d = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
      const key = d.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
      const sortKey = `${d.getFullYear()}-${String(d.getMonth()).padStart(2, "0")}`;
      if (!map[key]) map[key] = { month: key, revenue: 0, orders: 0, discount: 0, sortKey };
      map[key].revenue += b.total || 0;
      map[key].orders += 1;
      map[key].discount += b.discount || 0;
    });
    return Object.values(map).sort((a, b) => b.sortKey.localeCompare(a.sortKey));
  }, [filteredBookings]);

  // Top customers
  const topCustomers = useMemo(() => {
    const map = {};
    filteredBookings.forEach((b) => {
      const key = b.customerName || b.customerEmail;
      if (!map[key]) map[key] = { name: key, email: b.customerEmail, orders: 0, spent: 0 };
      map[key].orders += 1;
      map[key].spent += b.total || 0;
    });
    return Object.values(map).sort((a, b) => b.spent - a.spent).slice(0, 10);
  }, [filteredBookings]);

  // Export CSV
  const exportCSV = () => {
    const headers = "Date,Booking #,Customer,Email,Items,Subtotal,Discount,Delivery Fee,Total,Method,Status\n";
    const rows = filteredBookings.map((b) => {
      const d = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
      const items = (b.items || []).map((i) => `${i.productName}${i.selectedSize ? ` (${i.selectedSize})` : ""} x${i.quantity}`).join("; ");
      return [
        d.toLocaleDateString("en-GB"),
        b.bookingNumber || b.id.slice(0, 8),
        `"${b.customerName || ""}"`,
        b.customerEmail || "",
        `"${items}"`,
        b.subtotal || 0,
        b.discount || 0,
        b.deliveryFee || 0,
        b.total || 0,
        b.deliveryMethod || "pickup",
        b.status,
      ].join(",");
    }).join("\n");

    const blob = new Blob([headers + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `chaitimecrunch-sales-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatDate = (ts) => {
    if (!ts) return "—";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };

  return (
    <div>
      <div className="section-header">
        <div>
          <h2>Financial Reports</h2>
          <p>Sales analytics, revenue breakdown & export</p>
        </div>
        <button className="btn btn-primary" onClick={exportCSV}>
          📥 Export CSV
        </button>
      </div>

      {/* Date Range Filters */}
      <div className="filters" style={{ marginBottom: "1.5rem" }}>
        {["today", "week", "month", "year", "all", "custom"].map((r) => (
          <button key={r} className={`filter-btn ${dateRange === r ? "active" : ""}`}
            onClick={() => setDateRange(r)}>
            {r === "today" ? "Today" : r === "week" ? "This Week" : r === "month" ? "This Month" : r === "year" ? "This Year" : r === "all" ? "All Time" : "Custom"}
          </button>
        ))}
        {dateRange === "custom" && (
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            <input type="date" className="form-input" style={{ width: 150 }} value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)} />
            <span style={{ color: "var(--cocoa)" }}>to</span>
            <input type="date" className="form-input" style={{ width: 150 }} value={customTo}
              onChange={(e) => setCustomTo(e.target.value)} />
          </div>
        )}
      </div>

      {/* Key Metrics */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Total Revenue</div>
          <div className="stat-value">Rs {totalRevenue.toLocaleString()}</div>
        </div>
        <div className="stat-card sage">
          <div className="stat-label">Orders</div>
          <div className="stat-value">{totalOrders}</div>
        </div>
        <div className="stat-card berry">
          <div className="stat-label">Avg Order Value</div>
          <div className="stat-value">Rs {avgOrderValue}</div>
        </div>
        <div className="stat-card mocha">
          <div className="stat-label">Total Discounts</div>
          <div className="stat-value">Rs {totalDiscount.toLocaleString()}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Delivery Fees Earned</div>
          <div className="stat-value">Rs {totalDeliveryFees.toLocaleString()}</div>
        </div>
        <div className="stat-card sage">
          <div className="stat-label">Pickup / Delivery</div>
          <div className="stat-value">{pickupOrders} / {deliveryOrders}</div>
        </div>
      </div>

      <div className="two-col" style={{ marginTop: "2rem" }}>
        {/* Product Breakdown */}
        <div>
          <h3 style={{ fontFamily: "'Playfair Display', serif", marginBottom: "1rem" }}>
            Product Performance
          </h3>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr><th>Product</th><th>Qty Sold</th><th>Orders</th><th>Revenue</th></tr>
              </thead>
              <tbody>
                {productStats.map((p) => (
                  <tr key={p.name}>
                    <td style={{ fontWeight: 600 }}>{p.name}</td>
                    <td>{p.quantity}</td>
                    <td>{p.orders}</td>
                    <td style={{ fontWeight: 700 }}>Rs {p.revenue.toLocaleString()}</td>
                  </tr>
                ))}
                {productStats.length === 0 && (
                  <tr><td colSpan="4" style={{ textAlign: "center", color: "var(--cocoa)" }}>No data for selected period</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Top Customers */}
        <div>
          <h3 style={{ fontFamily: "'Playfair Display', serif", marginBottom: "1rem" }}>
            Top Customers
          </h3>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr><th>Customer</th><th>Orders</th><th>Total Spent</th></tr>
              </thead>
              <tbody>
                {topCustomers.map((c) => (
                  <tr key={c.email}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{c.name}</div>
                      <div style={{ fontSize: "0.78rem", color: "var(--cocoa)" }}>{c.email}</div>
                    </td>
                    <td>{c.orders}</td>
                    <td style={{ fontWeight: 700 }}>Rs {c.spent.toLocaleString()}</td>
                  </tr>
                ))}
                {topCustomers.length === 0 && (
                  <tr><td colSpan="3" style={{ textAlign: "center", color: "var(--cocoa)" }}>No data for selected period</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Monthly Breakdown */}
      <div style={{ marginTop: "2rem" }}>
        <h3 style={{ fontFamily: "'Playfair Display', serif", marginBottom: "1rem" }}>
          Monthly Breakdown
        </h3>
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr><th>Month</th><th>Orders</th><th>Revenue</th><th>Discounts Given</th><th>Net Revenue</th></tr>
            </thead>
            <tbody>
              {monthlyRevenue.map((m) => (
                <tr key={m.month}>
                  <td style={{ fontWeight: 600 }}>{m.month}</td>
                  <td>{m.orders}</td>
                  <td>Rs {m.revenue.toLocaleString()}</td>
                  <td style={{ color: "var(--sage-dark)" }}>Rs {m.discount.toLocaleString()}</td>
                  <td style={{ fontWeight: 700 }}>Rs {(m.revenue - m.discount).toLocaleString()}</td>
                </tr>
              ))}
              {monthlyRevenue.length === 0 && (
                <tr><td colSpan="5" style={{ textAlign: "center", color: "var(--cocoa)" }}>No data for selected period</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Daily Breakdown */}
      <div style={{ marginTop: "2rem" }}>
        <h3 style={{ fontFamily: "'Playfair Display', serif", marginBottom: "1rem" }}>
          Daily Breakdown
        </h3>
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr><th>Date</th><th>Orders</th><th>Revenue</th></tr>
            </thead>
            <tbody>
              {dailyRevenue.slice(0, 30).map((d) => (
                <tr key={d.date}>
                  <td style={{ fontWeight: 600 }}>{d.date}</td>
                  <td>{d.orders}</td>
                  <td style={{ fontWeight: 700 }}>Rs {d.revenue.toLocaleString()}</td>
                </tr>
              ))}
              {dailyRevenue.length === 0 && (
                <tr><td colSpan="3" style={{ textAlign: "center", color: "var(--cocoa)" }}>No data for selected period</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* All Transactions */}
      <div style={{ marginTop: "2rem" }}>
        <h3 style={{ fontFamily: "'Playfair Display', serif", marginBottom: "1rem" }}>
          All Transactions ({filteredBookings.length})
        </h3>
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr><th>Date</th><th>Booking #</th><th>Customer</th><th>Items</th><th>Subtotal</th><th>Discount</th><th>Total</th></tr>
            </thead>
            <tbody>
              {filteredBookings.slice(0, 50).map((b) => (
                <tr key={b.id}>
                  <td style={{ fontSize: "0.85rem" }}>{formatDate(b.createdAt)}</td>
                  <td style={{ fontFamily: "monospace", fontSize: "0.82rem" }}>{b.bookingNumber || b.id.slice(0, 8)}</td>
                  <td>{b.customerName}</td>
                  <td style={{ fontSize: "0.82rem" }}>
                    {(b.items || []).map((i, idx) => (
                      <div key={idx}>{i.productName}{i.selectedSize ? ` (${i.selectedSize})` : ""} ×{i.quantity}</div>
                    ))}
                  </td>
                  <td>Rs {b.subtotal || 0}</td>
                  <td style={{ color: b.discount > 0 ? "var(--sage-dark)" : "var(--cocoa)" }}>
                    {b.discount > 0 ? `-Rs ${b.discount}` : "—"}
                  </td>
                  <td style={{ fontWeight: 700 }}>Rs {b.total || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredBookings.length > 50 && (
          <div style={{ textAlign: "center", padding: "1rem", color: "var(--cocoa)", fontSize: "0.88rem" }}>
            Showing 50 of {filteredBookings.length} transactions. Export CSV for full data.
          </div>
        )}
      </div>
    </div>
  );
}